import { useState, useEffect } from "react"
import { useLocation, useNavigate } from "react-router"
import { motion } from "motion/react"
import { User01 } from "@untitledui/icons"
import { toast } from "sonner"

import { authApi } from "@/lib/api/auth"
import { usersApi } from "@/lib/api/users"
import { useAuth } from "@/lib/auth/auth-context"
import { WelcomeShell, WelcomeCard } from "@/components/welcome/welcome-shell"
import {
  Field,
  PrimaryButton,
  onboardingInputClass,
} from "@/components/onboarding/onboarding-shell"
import { readWelcomeNavState } from "@/lib/welcome/nav-state"

/**
 * Step 2 — confirm your name. Always shown: the JWT doesn't carry name
 * fields, so we can't cheaply tell whether the inviter pre-filled them
 * without an extra round trip. On mount we fetch /users/me directly so the
 * form pre-fills with whatever the inviter set.
 *
 * Team/role context comes from `location.state` (forwarded by the previous
 * page in the chain). On a hard refresh we drop back to `/` since the
 * personalised badge is what makes this page distinct from a generic
 * "edit your name" form.
 */
export default function WelcomeProfilePage() {
  const location = useLocation()
  const navigate = useNavigate()
  const { refreshUser } = useAuth()

  const navState = readWelcomeNavState(location.state)

  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [hadPrefill, setHadPrefill] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState("")

  useEffect(() => {
    let active = true
    usersApi
      .fetchProfile()
      .then((p) => {
        if (!active) return
        if (p.firstName) setFirstName(p.firstName)
        if (p.lastName) setLastName(p.lastName)
        setHadPrefill(Boolean(p.firstName || p.lastName))
      })
      .catch(() => {
        // Profile fetch is best-effort: a transient network error shouldn't
        // block the user from typing their name.
      })
      .finally(() => {
        if (active) setIsLoading(false)
      })
    return () => {
      active = false
    }
  }, [])

  // No nav context = user got here outside the flow (refresh, direct URL).
  // Send them home rather than show a confusing empty welcome.
  useEffect(() => {
    if (!navState && !isLoading) {
      navigate("/", { replace: true })
    }
  }, [navState, isLoading, navigate])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError("")
    if (!firstName.trim()) {
      setError("Your first name is required.")
      return
    }

    setIsSubmitting(true)
    try {
      await authApi.updateProfile({ firstName: firstName.trim(), lastName: lastName.trim() })
      await refreshUser()
      navigate("/welcome/role-tour", { replace: true, state: navState })
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't save your name.")
      toast.error("Couldn't save your name.")
    } finally {
      setIsSubmitting(false)
    }
  }

  const team = navState
    ? { name: navState.teamName, role: navState.role }
    : undefined

  return (
    <WelcomeShell step="profile" team={team}>
      <WelcomeCard
        eyebrow="Step 2 of 3"
        title={hadPrefill ? "Confirm your name." : "How should we address you?"}
        subtitle={
          hadPrefill
            ? "Your invite came with a name attached. If anything's off, fix it now."
            : "Two quick fields and we'll move on to your tour."
        }
        hero={<ProfileHero firstName={firstName} lastName={lastName} />}
      >
        <form onSubmit={handleSubmit} className="space-y-5">
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              className="rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-[13px] text-red-700"
            >
              {error}
            </motion.div>
          )}

          <Field label="First name" required>
            <input
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="Your first name"
              className={onboardingInputClass}
              autoFocus
              autoComplete="given-name"
              required
              maxLength={60}
            />
          </Field>

          <Field label="Last name">
            <input
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Your last name"
              className={onboardingInputClass}
              autoComplete="family-name"
              maxLength={60}
            />
          </Field>

          <div className="pt-2">
            <PrimaryButton
              type="submit"
              loading={isSubmitting}
              disabled={!firstName.trim()}
              size="wide"
              variant="dark"
            >
              Continue
            </PrimaryButton>
          </div>
        </form>
      </WelcomeCard>
    </WelcomeShell>
  )
}

function ProfileHero({ firstName, lastName }: { firstName: string; lastName: string }) {
  const display = [firstName, lastName].filter(Boolean).join(" ") || "—"
  const initials =
    [firstName, lastName]
      .map((n) => n.trim()[0])
      .filter(Boolean)
      .join("")
      .toUpperCase() || "—"

  return (
    <div className="w-full max-w-sm space-y-5">
      <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-emerald-600">
        Quick check
      </p>
      <div className="flex items-center gap-4">
        <div className="size-14 rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-600 grid place-items-center text-white font-bold text-lg shadow-lg shadow-emerald-600/20">
          {initials}
        </div>
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-zinc-400">
            How you'll appear
          </p>
          <p className="text-[18px] font-semibold text-zinc-900 truncate">{display}</p>
        </div>
      </div>
      <div className="flex items-start gap-2.5 text-[13px] text-zinc-500">
        <User01 className="mt-0.5 size-4 text-zinc-400 shrink-0" aria-hidden />
        <span>This is what your team sees on estimates and reports.</span>
      </div>
    </div>
  )
}
